import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, finalize, tap } from 'rxjs';
import { GradingApiService } from './grading-api.service';
import { GradingStateService } from './grading-state.service';
import { GradingSession } from '../grading.types';
import { AmplitudeService } from '../../../../common/analytics/amplitude.service';
import { AuthService } from '../../../../common/auth/auth.service';

/**
 * Session storage key for recently tracked session (with timestamp)
 * Format: { sessionId: string, timestamp: number }
 */
const TRACKED_SESSION_KEY = 'grading_recently_tracked';

/** Time window (ms) to consider a session as "just tracked" - prevents duplicate on create → redirect */
const DUPLICATE_PREVENTION_WINDOW_MS = 5000;

/**
 * Service for managing grading session lifecycle.
 * Handles session creation, loading, and cleanup.
 */
@Injectable()
export class GradingSessionService {
  private readonly gradingApiService = inject(GradingApiService);
  private readonly stateService = inject(GradingStateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly authService = inject(AuthService);

  /**
   * Get current session ID from state
   */
  get sessionId(): string | null {
    return this.stateService.sessionId;
  }

  /**
   * Create a new grading session
   * Returns an Observable that emits the created session
   */
  createSession(): Observable<GradingSession> {
    this.stateService.setLoading(true);
    this.stateService.setError(null);

    return this.gradingApiService.createGrading().pipe(
      tap({
        next: (session) => {
          this.stateService.setSession(session);
          this.stateService.setLoading(false);
          this.trackSessionLoaded(session, true);
        },
        error: (err) => {
          console.error('Failed to create grading session:', err);
          this.stateService.setError(
            'Failed to start grading session. Please try again.'
          );
          this.stateService.setLoading(false);
        },
      })
    );
  }

  /**
   * Load an existing grading session by ID
   */
  loadSession(sessionId: string): void {
    this.stateService.setLoading(true);
    this.stateService.setError(null);

    this.gradingApiService
      .getGrading(sessionId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.stateService.setLoading(false))
      )
      .subscribe({
        next: (session) => {
          this.stateService.setSession(session);
          this.trackSessionLoaded(session, false);
        },
        error: (err) => {
          console.error('Failed to load grading session:', err);
          this.stateService.setError(
            'Failed to load grading session. Please try again.'
          );
        },
      });
  }

  /**
   * Clear current session
   */
  clearSession(): void {
    this.stateService.clearState();
  }

  /**
   * Track grading session loaded event (only once per navigation flow)
   */
  private trackSessionLoaded(session: GradingSession, isNewSession: boolean): void {
    // Skip if we've recently tracked this session (prevents duplicate on create -> redirect)
    if (this.isSessionRecentlyTracked(session.id)) {
      return;
    }
    this.markSessionTracked(session.id);

    this.amplitudeService.trackEvent('grading_session_loaded', {
      sessionId: session.id,
      isNewSession,
      isAuthenticated: !!this.authService.getToken(),
      hasResources: (session.resources?.length ?? 0) > 0,
      messageCount: session.messages?.length ?? 0,
    });
  }

  /**
   * Check if a session was recently tracked (within the duplicate prevention window)
   * This prevents duplicate events during create → redirect, but allows tracking on page reload
   */
  private isSessionRecentlyTracked(sessionId: string): boolean {
    try {
      const tracked = sessionStorage.getItem(TRACKED_SESSION_KEY);
      if (!tracked) return false;

      const data = JSON.parse(tracked) as { sessionId: string; timestamp: number };
      if (data.sessionId !== sessionId) return false;

      // Check if within the time window
      const elapsed = Date.now() - data.timestamp;
      return elapsed < DUPLICATE_PREVENTION_WINDOW_MS;
    } catch {
      return false;
    }
  }

  /**
   * Mark a session as recently tracked (with timestamp)
   */
  private markSessionTracked(sessionId: string): void {
    try {
      const data = { sessionId, timestamp: Date.now() };
      sessionStorage.setItem(TRACKED_SESSION_KEY, JSON.stringify(data));
    } catch {
      // Ignore storage errors
    }
  }
}
